import { clients } from 'lib/https';
import { getConfigurationSettings } from 'services/configuration-service';

import type {
  IGetAgentModelItem,
  IUsageEnforcementConfig,
  IUsageEnforcementPayload,
  IUsageRuleUpsert,
  IUsageRuleUpsertResult,
} from '../types/usage-enforcement.model';

const urls = async () => {
  const config = await getConfigurationSettings();
  const base = config.paths.seliseAiBaseUrl;
  return {
    blocksKey: config.blocksKey,
    tenant: `${base}${config.paths.seliseAi.usageEnforcement}`,
    organizations: `${base}${config.paths.seliseAi.usageEnforcementOrganizations}`,
    rules: `${base}${config.paths.seliseAi.usageEnforcementRules}`,
    // Every type a charge can pay for -- image, speech and rerank included -- not only chat,
    // and every model whatever is restricted: this is where the restriction is chosen.
    models: `${base}${config.paths.seliseAi.agentModels}?all_types=true&include_restricted=true`,
  };
};

export const usageEnforcementService = {
  async getEnforcement(): Promise<IUsageEnforcementConfig> {
    const { tenant, blocksKey } = await urls();
    return clients.get(tenant, blocksKey);
  },

  async saveEnforcement(payload: IUsageEnforcementPayload): Promise<IUsageEnforcementConfig> {
    const { tenant, blocksKey } = await urls();
    return clients.put(tenant, blocksKey, JSON.stringify(payload));
  },

  /** Organizations that keep settings of their own; everything else follows the tenant. */
  async getConfiguredOrganizations(): Promise<string[]> {
    const { organizations, blocksKey } = await urls();
    return clients.get(organizations, blocksKey);
  },

  async getOrganizationEnforcement(organizationId: string): Promise<IUsageEnforcementConfig> {
    const { organizations, blocksKey } = await urls();
    return clients.get(`${organizations}/${organizationId}`, blocksKey);
  },

  async saveOrganizationEnforcement(
    organizationId: string,
    payload: IUsageEnforcementPayload
  ): Promise<IUsageEnforcementConfig> {
    const { organizations, blocksKey } = await urls();
    return clients.put(`${organizations}/${organizationId}`, blocksKey, JSON.stringify(payload));
  },

  /** Rules and metadata, the business side's half. Each item answers for itself. */
  async upsertRules(items: IUsageRuleUpsert[]): Promise<IUsageRuleUpsertResult[]> {
    const { rules, blocksKey } = await urls();
    const response: { results: IUsageRuleUpsertResult[] } = await clients.post(
      rules,
      blocksKey,
      JSON.stringify({ items })
    );
    const failed = response.results.find((result) => !result.ok);
    if (failed) throw new Error(failed.error);
    return response.results;
  },


  async getModels(): Promise<IGetAgentModelItem[]> {
    const { models, blocksKey } = await urls();
    return clients.get(models, blocksKey);
  },
};
